import { useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";

import utils from "../utils";
import { flushUiPrefs, setIntentsColumnPrefs } from "../../slices/thunk";

// Stable reference, so components don't re-render on every unrelated store touch.
const EMPTY = Object.freeze({});

/**
 * The user's transactions-table column layout, plus the mutators the editor uses.
 *
 * `order` / `hidden` here are the *resolved* values for the current org config --
 * pass them straight to utils.resolveColumnLayout's caller. Every mutator writes
 * BOTH keys, which is what turns "no opinion" into "the org default, now owned by
 * this user" on their first interaction.
 */
export const useIntentsColumnPrefs = () => {
  const dispatch = useDispatch();
  const prefs = useSelector((s) => s.UiPrefs?.prefs?.intents_columns) || EMPTY;

  // Whatever is pending when the component dies still gets written: five of the
  // six tables are inside expandable rows that unmount on collapse.
  useEffect(() => () => dispatch(flushUiPrefs()), [dispatch]);

  const commit = useCallback(
    (order, hidden) =>
      dispatch(setIntentsColumnPrefs({ order: [...order], hidden: [...hidden] })),
    [dispatch]
  );

  const moveColumn = useCallback(
    (order, hidden, key, dir) => {
      const i = order.indexOf(key);
      const j = i + dir;
      if (i < 0 || j < 0 || j >= order.length) return;
      const next = [...order];
      [next[i], next[j]] = [next[j], next[i]];
      commit(next, hidden);
    },
    [commit]
  );

  const toggleColumn = useCallback(
    (order, hidden, key) => {
      // Belt to the server's braces (clean_ui_prefs strips these too).
      if (utils.INTENTS_LOCKED_COLUMNS.includes(key)) return;
      const next = new Set(hidden);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      commit(order, next);
    },
    [commit]
  );

  // Drop the user's opinion entirely; both fallbacks in resolveColumnLayout
  // re-engage and the org default applies again.
  const resetColumns = useCallback(
    () => dispatch(setIntentsColumnPrefs({})),
    [dispatch]
  );

  return { prefs, moveColumn, toggleColumn, resetColumns };
};
